import { createContext, useContext, type AnchorHTMLAttributes, type ReactNode } from 'react';

/** Props handed to `renderLink`; spread them onto the router's link element. */
export type RenderLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; children: ReactNode };
/** Renders a navigation link (e.g. with the app router's `Link`). Defaults to a plain `<a>`. */
export type RenderLink = (props: RenderLinkProps) => ReactNode;

export interface EntityPeekRequest {
  entityType: string;
  id: string;
  label?: string;
}

export interface EntityLinkContextValue {
  renderLink?: RenderLink;
  /** Opens the entity in a peek drawer; injected once by the app shell. */
  onPeek?: (request: EntityPeekRequest) => void;
}

const EntityLinkContext = createContext<EntityLinkContextValue>({});

/** Supplies the router link and the peek-drawer handler to every `EntityLink` below. */
export function EntityLinkProvider({ renderLink, onPeek, children }: EntityLinkContextValue & { children: ReactNode }) {
  return <EntityLinkContext.Provider value={{ renderLink, onPeek }}>{children}</EntityLinkContext.Provider>;
}

export function useEntityLinkContext(): EntityLinkContextValue {
  return useContext(EntityLinkContext);
}

export const defaultRenderLink: RenderLink = ({ href, children, ...rest }) => (
  <a href={href} {...rest}>
    {children}
  </a>
);
